import React, { useState, useEffect } from 'react';
import { KeyRound, Shield, Loader2, AlertCircle, Check, Smartphone, Copy, ToggleLeft, ToggleRight, Flag, Gauge, Save, Infinity, Bell, Play, Monitor, Globe, Gift, BookOpen, Clock, Image, Share2, Upload } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import { Button } from '@/components/ui/button';
import { useAdmin } from '@/contexts/AdminContext';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


export default function AdminSettingsPage() {
  const { admin } = useAdmin();
  const { colors } = useAdminTheme();
  const isSuperAdmin = admin?.role === 'super_admin';

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl" data-testid="admin-settings-page">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Settings</h1>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Manage account security and feature flags</p>
        </div>

        <UsageLimitsSection />
        <UsageAlertsSection />
        <URLPreviewSection />
        <ReflectionScheduleSection />
        <GoogleSignInSection />
        <GuestTrialSection />
        <AutoNotificationsSection />
        <ReflectionNotifSection />
        <AdminPushSection />
        <FeatureFlagsSection />
        {isSuperAdmin && <AdminDeviceLimitsSection />}
        {isSuperAdmin && <InactivityTimeoutSection />}
        <ChangePasswordSection />
        <Reset2FASection admin={admin} />
      </div>
    </AdminLayout>
  );
}

function ChangePasswordSection() {
  const { isDark, colors } = useAdminTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        totp_code: totpCode,
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTotpCode('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: colors.input,
    borderColor: colors.inputBorder,
    color: colors.text,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="change-password-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(79,70,229,0.08)' }}>
          <KeyRound className="h-4.5 w-4.5 text-indigo-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Change Password</h3>
          <p className="text-xs" style={{ color: colors.textMuted }}>Requires your current password and 2FA code</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Current Password</label>
          <PasswordInput
            value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
            className="w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:border-indigo-500 transition"
            style={inputStyle}
            placeholder="Enter current password" required
            data-testid="current-password-input"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>New Password</label>
            <PasswordInput
              value={newPassword} onChange={e => setNewPassword(e.target.value)}
              className="w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:border-indigo-500 transition"
              style={inputStyle}
              placeholder="Min 8 chars, 1 uppercase, 1 number" required
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Confirm New Password</label>
            <PasswordInput
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              className="w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:border-indigo-500 transition"
              style={inputStyle}
              placeholder="Re-enter new password" required
              data-testid="confirm-password-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>2FA Code</label>
          <input
            type="text" inputMode="numeric" maxLength={6}
            value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
            className="w-full max-w-[200px] rounded-xl border px-4 py-2.5 text-sm font-mono tracking-widest focus:outline-none focus:border-indigo-500 transition"
            style={inputStyle}
            placeholder="000000" required
            data-testid="change-pw-totp-input"
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="change-pw-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        <Button
          type="submit" disabled={loading || totpCode.length !== 6}
          className="rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white gap-2"
          data-testid="change-password-submit"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
          Change Password
        </Button>
      </form>
    </div>
  );
}

function Reset2FASection({ admin }) {
  const { isDark, colors } = useAdminTheme();
  const [showReset, setShowReset] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const { verify2FA, logout } = useAdmin();

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/auth/reset-2fa`, {
        email: admin?.email,
        password,
      });
      setQrData(res.data);
      setPassword('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FA(qrData.admin_id, totpCode, qrData.setup_token);
      toast.success('2FA has been reset and verified');
      setShowReset(false);
      setQrData(null);
      setTotpCode('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(qrData.totp_secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const inputStyle = {
    background: colors.input,
    borderColor: colors.inputBorder,
    color: colors.text,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="reset-2fa-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)' }}>
          <Smartphone className="h-4.5 w-4.5 text-amber-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Two-Factor Authentication</h3>
          <p className="text-xs" style={{ color: colors.textMuted }}>Reset your authenticator if you lost access</p>
        </div>
      </div>

      {!showReset && !qrData && (
        <Button
          onClick={() => setShowReset(true)}
          variant="outline"
          className="rounded-xl border-amber-500/30 text-amber-400 hover:bg-amber-500/10 gap-2"
          data-testid="reset-2fa-btn"
        >
          <Shield className="h-4 w-4" /> Reset 2FA
        </Button>
      )}

      {showReset && !qrData && (
        <form onSubmit={handleReset} className="space-y-4 max-w-sm">
          <p className="text-xs" style={{ color: colors.textSecondary }}>Enter your password to confirm identity and generate a new QR code.</p>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Password</label>
            <PasswordInput
              value={password} onChange={e => setPassword(e.target.value)}
              className="w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:border-indigo-500 transition"
              style={inputStyle}
              placeholder="Enter your password" required
              data-testid="reset-2fa-password"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={() => { setShowReset(false); setError(''); }} variant="ghost" className="rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-xl bg-amber-600 hover:bg-amber-500 text-white gap-2" data-testid="reset-2fa-confirm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
              Reset 2FA
            </Button>
          </div>
        </form>
      )}

      {qrData && (
        <form onSubmit={handleVerify} className="space-y-5 max-w-sm">
          <div className="text-center">
            <p className="text-sm mb-3" style={{ color: colors.textSecondary }}>Scan this new QR code with your authenticator app</p>
            <div className="inline-block bg-white p-3 rounded-xl" data-testid="reset-qr-code">
              <img src={`data:image/png;base64,${qrData.qr_code}`} alt="2FA QR Code" className="w-48 h-48" />
            </div>
          </div>

          <div>
            <p className="text-[11px] text-center mb-1.5" style={{ color: colors.textMuted }}>Or enter manually:</p>
            <div className="flex items-center justify-center gap-2 rounded-xl px-3 py-2" style={{ background: colors.input }}>
              <code className="text-xs text-indigo-400 font-mono tracking-widest">{qrData.totp_secret}</code>
              <button type="button" onClick={copySecret} className="p-1 transition" style={{ color: colors.textMuted }}>
                {secretCopied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Verification Code</label>
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-xl border px-4 py-3 text-center text-lg font-mono tracking-[0.3em] focus:outline-none focus:border-indigo-500 transition"
              style={inputStyle}
              placeholder="000000" autoFocus required
              data-testid="reset-2fa-totp-input"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white gap-2" data-testid="reset-2fa-verify">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Verify & Activate
          </Button>
        </form>
      )}
    </div>
  );
}


function UsageLimitsSection() {
  const { isDark, colors } = useAdminTheme();
  const [limits, setLimits] = useState({ free: 25, paid: -1, premium: -1, guest: 5 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [guestMode, setGuestMode] = useState('per_device');
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/usage-limits`, { withCredentials: true })
      .then(res => { setLimits(res.data); setGuestMode(res.data.guest_limit_mode || 'per_device'); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleChange = (tier, value) => {
    const parsed = value === '' ? '' : parseInt(value, 10);
    setLimits(prev => ({ ...prev, [tier]: parsed }));
    setDirty(true);
  };

  const toggleUnlimited = (tier) => {
    setLimits(prev => ({
      ...prev,
      [tier]: prev[tier] === -1 ? 50 : -1
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    const payload = {};
    for (const tier of ['free', 'paid', 'premium', 'guest']) {
      const val = limits[tier];
      if (val === '' || val === 0 || (typeof val === 'number' && val < -1)) {
        toast.error(`Invalid limit for ${tier}. Use a positive number or set to unlimited.`);
        return;
      }
      payload[tier] = typeof val === 'number' ? val : parseInt(val, 10);
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/usage-limits`, payload, { withCredentials: true });
      toast.success('Usage limits updated — changes are live immediately');
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update limits');
    }
    setSaving(false);
  };

  const TIER_META = [
    { key: 'guest', label: 'Guest', color: '#94a3b8', bg: isDark ? 'rgba(148,163,184,0.1)' : 'rgba(100,116,139,0.08)' },
    { key: 'free', label: 'Free', color: colors.textSecondary, bg: isDark ? 'rgba(148,163,184,0.1)' : 'rgba(100,116,139,0.08)' },
    { key: 'paid', label: 'Standard (Legacy)', color: '#818cf8', bg: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(79,70,229,0.08)' },
    { key: 'premium', label: 'Premium ($9.99/mo)', color: '#fbbf24', bg: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)' },
  ];

  const rowStyle = {
    background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="usage-limits-section">
      <div className="flex items-center gap-2 mb-1">
        <Gauge className="h-5 w-5 text-emerald-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Daily Message Limits</h2>
      </div>
      <p className="text-xs mb-5" style={{ color: colors.textMuted }}>Set the maximum AI responses per day for each subscription tier. Changes apply immediately.</p>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />
      ) : (
        <>
          <div className="space-y-3">
            {TIER_META.map(t => (
              <div key={t.key} className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid={`limit-row-${t.key}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: t.bg }}>
                    {limits[t.key] === -1 ? <Infinity className="h-4 w-4" style={{ color: t.color }} /> : <span className="text-xs font-bold" style={{ color: t.color }}>{limits[t.key]}</span>}
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: t.color }}>{t.label}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{limits[t.key] === -1 ? 'Unlimited messages' : `${limits[t.key] || 0} messages/day`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {limits[t.key] !== -1 && (
                    <input
                      type="number"
                      min="1"
                      max="9999"
                      value={limits[t.key] === '' ? '' : limits[t.key]}
                      onChange={e => handleChange(t.key, e.target.value)}
                      className="w-20 rounded-lg border px-3 py-1.5 text-sm text-center focus:outline-none focus:border-indigo-500 transition"
                      style={{ background: colors.input, borderColor: colors.inputBorder, color: colors.text }}
                      data-testid={`limit-input-${t.key}`}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => toggleUnlimited(t.key)}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition border"
                    style={limits[t.key] === -1
                      ? { background: 'rgba(16,185,129,0.2)', color: '#34d399', borderColor: 'rgba(16,185,129,0.3)' }
                      : { background: colors.input, color: colors.textSecondary, borderColor: colors.inputBorder }
                    }
                    data-testid={`limit-unlimited-toggle-${t.key}`}
                  >
                    {limits[t.key] === -1 ? 'Unlimited' : 'Set Unlimited'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Guest Limit Mode Toggle */}
          <div className="mt-5 p-4 rounded-xl" style={rowStyle} data-testid="guest-limit-mode-section">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Guest Limit Mode</p>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                  {guestMode === 'per_device'
                    ? 'Per Device — guests get a fixed total of messages per device (never resets)'
                    : 'Per Day — guests get messages that reset daily at midnight UTC'}
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-lg p-0.5 shrink-0 ml-4" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#e2e8f0' }}>
                <button
                  onClick={async () => {
                    setSavingMode(true);
                    try {
                      await axios.put(`${API}/admin/guest-limit-mode`, { guest_limit_mode: 'per_device' }, { withCredentials: true });
                      setGuestMode('per_device');
                      toast.success('Guest limit mode set to Per Device');
                    } catch { toast.error('Failed to update mode'); }
                    setSavingMode(false);
                  }}
                  disabled={savingMode}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all"
                  style={guestMode === 'per_device'
                    ? { background: 'rgba(99,102,241,0.2)', color: '#818cf8' }
                    : { color: colors.textMuted }}
                  data-testid="guest-mode-per-device"
                >
                  Per Device
                </button>
                <button
                  onClick={async () => {
                    setSavingMode(true);
                    try {
                      await axios.put(`${API}/admin/guest-limit-mode`, { guest_limit_mode: 'per_day' }, { withCredentials: true });
                      setGuestMode('per_day');
                      toast.success('Guest limit mode set to Per Day');
                    } catch { toast.error('Failed to update mode'); }
                    setSavingMode(false);
                  }}
                  disabled={savingMode}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all"
                  style={guestMode === 'per_day'
                    ? { background: 'rgba(99,102,241,0.2)', color: '#818cf8' }
                    : { color: colors.textMuted }}
                  data-testid="guest-mode-per-day"
                >
                  Per Day
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white gap-2 disabled:opacity-40"
              data-testid="save-usage-limits-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Limits
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function UsageAlertsSection() {
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState({ enabled: false, threshold_percent: 50, last_alert_at: null, last_alert_result: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/usage-alerts/config`, { withCredentials: true })
      .then(res => { setConfig(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/usage-alerts/config`, {
        enabled: config.enabled,
        threshold_percent: config.threshold_percent,
      }, { withCredentials: true });
      toast.success('Alert settings saved');
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/admin/usage-alerts/check-now`, {}, { withCredentials: true });
      const r = res.data;
      if (r.alert_sent) {
        toast.success(`Alert sent! ${r.current_percent}% of users at limit (${r.at_limit}/${r.active_users})`);
      } else if (r.exceeded === false) {
        toast.info(`Below threshold: ${r.current_percent}% at limit (threshold: ${r.threshold}%)`);
      } else {
        toast.info(r.reason || 'Check complete — no alert needed');
      }
      setConfig(prev => ({ ...prev, last_alert_at: new Date().toISOString(), last_alert_result: r }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check failed');
    }
    setChecking(false);
  };

  const lastResult = config.last_alert_result;

  const rowStyle = {
    background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="usage-alerts-section">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="h-5 w-5 text-amber-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Usage Limit Alerts</h2>
      </div>
      <p className="text-xs mb-5" style={{ color: colors.textMuted }}>Get email alerts when too many free users hit their daily limit. Runs daily at 11:55 PM UTC.</p>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />
      ) : (
        <>
          <div className="space-y-4">
            {/* Enable/disable toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid="alert-enabled-row">
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Email Alerts</p>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Send alert to all admins when threshold is exceeded</p>
              </div>
              <button
                onClick={() => { setConfig(prev => ({ ...prev, enabled: !prev.enabled })); setDirty(true); }}
                className="shrink-0 ml-4 transition-colors"
                data-testid="alert-enabled-toggle"
              >
                {config.enabled ? <ToggleRight className="h-7 w-7 text-green-400" /> : <ToggleLeft className="h-7 w-7" style={{ color: colors.textMuted }} />}
              </button>
            </div>

            {/* Threshold */}
            <div className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid="alert-threshold-row">
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Alert Threshold</p>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Trigger when this % of active free users hit their daily limit</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" max="100"
                  value={config.threshold_percent}
                  onChange={e => { setConfig(prev => ({ ...prev, threshold_percent: parseInt(e.target.value, 10) || 50 })); setDirty(true); }}
                  className="w-16 rounded-lg border px-3 py-1.5 text-sm text-center focus:outline-none focus:border-indigo-500 transition"
                  style={{ background: colors.input, borderColor: colors.inputBorder, color: colors.text }}
                  data-testid="alert-threshold-input"
                />
                <span className="text-sm" style={{ color: colors.textSecondary }}>%</span>
              </div>
            </div>

            {/* Last check result */}
            {lastResult && (
              <div className="p-3 rounded-xl" style={rowStyle} data-testid="alert-last-result">
                <p className="text-xs mb-2" style={{ color: colors.textMuted }}>Last check: {config.last_alert_at ? new Date(config.last_alert_at).toLocaleString() : 'Never'}</p>
                <div className="flex items-center gap-3 text-xs">
                  {lastResult.alert_sent ? (
                    <span className="px-2 py-1 rounded-md bg-amber-500/15 text-amber-400 font-medium">Alert sent to {lastResult.recipients} admin(s)</span>
                  ) : lastResult.exceeded === false ? (
                    <span className="px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-400 font-medium">Below threshold</span>
                  ) : (
                    <span className="px-2 py-1 rounded-md font-medium" style={{ background: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(100,116,139,0.1)', color: colors.textSecondary }}>{lastResult.reason || 'No alert needed'}</span>
                  )}
                  {lastResult.current_percent !== undefined && (
                    <span style={{ color: colors.textSecondary }}>{lastResult.current_percent}% at limit ({lastResult.at_limit}/{lastResult.active_users} users)</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-5 flex justify-between">
            <Button
              onClick={handleCheckNow}
              disabled={checking}
              variant="outline"
              className="rounded-xl border-amber-500/30 text-amber-400 hover:bg-amber-500/10 gap-2"
              data-testid="check-now-btn"
            >
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Check Now
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white gap-2 disabled:opacity-40"
              data-testid="save-alert-config-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Settings
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function GoogleSignInSection() {
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState({ enabled: true, show_password_reset_for_google_users: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/google-login-config`, { withCredentials: true })
      .then(res => { setConfig(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleToggle = (key) => {
    setConfig(prev => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/google-login-config`, config, { withCredentials: true });
      toast.success('Google sign-in settings saved');
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const rowStyle = {
    background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="google-signin-section">
      <div className="flex items-center gap-2 mb-1">
        <Globe className="h-5 w-5 text-blue-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Google Sign-In</h2>
      </div>
      <p className="text-xs mb-5" style={{ color: colors.textMuted }}>Control Google OAuth sign-in availability for users</p>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />
      ) : (
        <>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid="google-signin-enabled-row">
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Allow Google Sign-In</p>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>When disabled, users can only sign in with email/password</p>
              </div>
              <button onClick={() => handleToggle('enabled')} className="shrink-0 ml-4 transition-colors" data-testid="google-signin-toggle">
                {config.enabled ? <ToggleRight className="h-7 w-7 text-green-400" /> : <ToggleLeft className="h-7 w-7" style={{ color: colors.textMuted }} />}
              </button>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid="google-password-reset-row">
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Password Reset for Google Users</p>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>Show option to set a password for Google-only accounts</p>
              </div>
              <button onClick={() => handleToggle('show_password_reset_for_google_users')} className="shrink-0 ml-4 transition-colors" data-testid="google-password-reset-toggle">
                {config.show_password_reset_for_google_users ? <ToggleRight className="h-7 w-7 text-green-400" /> : <ToggleLeft className="h-7 w-7" style={{ color: colors.textMuted }} />}
              </button>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="rounded-xl bg-blue-600 hover:bg-blue-500 text-white gap-2 disabled:opacity-40"
              data-testid="save-google-signin-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Settings
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function GuestTrialSection() {
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState({ enabled: false, trial_tier: 'premium', trial_duration_days: 7 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/guest-trial-config`, { withCredentials: true })
      .then(res => { setConfig(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/guest-trial-config`, config, { withCredentials: true });
      toast.success('Guest trial settings saved');
      setDirty(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const rowStyle = {
    background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="guest-trial-section">
      <div className="flex items-center gap-2 mb-1">
        <Gift className="h-5 w-5 text-amber-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Guest Free Trial</h2>
      </div>
      <p className="text-xs mb-5" style={{ color: colors.textMuted }}>Show a free trial option when guest users hit their daily message limit</p>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />
      ) : (
        <>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid="guest-trial-enabled-row">
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Enable Free Trial for Guests</p>
                <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>When enabled, guests see a "Start Free Trial" button when they hit their message limit</p>
              </div>
              <button onClick={() => { setConfig(prev => ({ ...prev, enabled: !prev.enabled })); setDirty(true); }} className="shrink-0 ml-4 transition-colors" data-testid="guest-trial-toggle">
                {config.enabled ? <ToggleRight className="h-7 w-7 text-green-400" /> : <ToggleLeft className="h-7 w-7" style={{ color: colors.textMuted }} />}
              </button>
            </div>

            {config.enabled && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: colors.textSecondary }}>Trial Plan</label>
                  <select value={config.trial_tier} onChange={e => { setConfig(prev => ({ ...prev, trial_tier: e.target.value })); setDirty(true); }}
                    className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none"
                    style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f1f5f9', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, color: colors.text }}
                    data-testid="guest-trial-tier">
                    <option value="premium">Premium ($9.99)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: colors.textSecondary }}>Duration (days)</label>
                  <input type="number" value={config.trial_duration_days}
                    onChange={e => { setConfig(prev => ({ ...prev, trial_duration_days: parseInt(e.target.value) || 7 })); setDirty(true); }}
                    min={1} max={30}
                    className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none"
                    style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f1f5f9', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, color: colors.text }}
                    data-testid="guest-trial-days" />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSave} disabled={saving || !dirty}
              className="rounded-xl bg-amber-600 hover:bg-amber-500 text-white gap-2 disabled:opacity-40"
              data-testid="save-guest-trial-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Settings
            </Button>
          </div>
        </>
      )}
    </div>
  );
}



function ReflectionScheduleSection() {
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState({ timezone: 'America/New_York', available_time: '00:00' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'America/Toronto', label: 'Eastern Time - Canada' },
    { value: 'America/Vancouver', label: 'Pacific Time - Canada' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central Europe (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Africa/Lagos', label: 'West Africa (WAT)' },
    { value: 'Africa/Nairobi', label: 'East Africa (EAT)' },
    { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
    { value: 'Asia/Jerusalem', label: 'Israel (IST)' },
    { value: 'Asia/Dubai', label: 'Gulf (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Japan (JST)' },
    { value: 'Asia/Seoul', label: 'Korea (KST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    axios.get(`${API}/admin/reflection-schedule`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reflection-schedule`, config, { withCredentials: true });
      toast.success('Reflection schedule saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    background: isDark ? 'rgba(15,23,42,0.5)' : '#fff',
    border: `1px solid ${colors.cardBorder}`,
    color: colors.text,
  };

  return (
    <div
      className="rounded-2xl p-5 space-y-5"
      style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid="reflection-schedule-section"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#f59e0b18' }}>
          <Clock className="h-4 w-4" style={{ color: '#f59e0b' }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Daily Reflection Schedule</h3>
          <p className="text-xs" style={{ color: colors.textMuted }}>Control when the daily reflection changes to a new day</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: colors.textMuted }} />
        </div>
      ) : (
        <div className="space-y-4 pt-1">
          {/* Timezone */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              <Globe className="h-3.5 w-3.5" />
              Timezone
            </label>
            <select
              value={config.timezone}
              onChange={e => update('timezone', e.target.value)}
              className="rounded-lg px-3 py-2 text-sm w-full appearance-none"
              style={inputStyle}
              data-testid="reflection-schedule-timezone"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          {/* Availability Time */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              <Clock className="h-3.5 w-3.5" />
              New Reflection Available At
            </label>
            <input
              type="time"
              value={config.available_time}
              onChange={e => update('available_time', e.target.value)}
              className="rounded-lg px-3 py-2 text-sm w-full"
              style={inputStyle}
              data-testid="reflection-schedule-time"
            />
            <p className="text-[11px] mt-1.5" style={{ color: colors.textMuted }}>
              The new daily reflection becomes available at this time in the selected timezone. Before this time, users will still see the previous day's reflection.
            </p>
          </div>

          {/* Save */}
          {dirty && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl px-4 py-2 text-sm font-medium gap-2"
              style={{ background: colors.accent, color: '#fff' }}
              data-testid="reflection-schedule-save"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function URLPreviewSection() {
  const { isDark, colors } = useAdminTheme();
  const [settings, setSettings] = useState({ title: '', description: '', image_path: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/og-settings`, { withCredentials: true })
      .then(r => {
        setSettings(r.data);
        if (r.data.image_path) {
          setPreviewUrl(`${API}/og-image?_t=${Date.now()}`);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => {
    setSettings(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/og-settings`, {
        title: settings.title,
        description: settings.description,
      }, { withCredentials: true });
      toast.success('URL preview settings saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/og-image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings(prev => ({ ...prev, image_path: res.data.image_path }));
      setPreviewUrl(`${API}/og-image?_t=${Date.now()}`);
      toast.success('Preview image updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const inputStyle = {
    background: isDark ? 'rgba(15,23,42,0.5)' : '#fff',
    border: `1px solid ${colors.cardBorder}`,
    color: colors.text,
  };

  return (
    <div
      className="rounded-2xl p-5 space-y-5"
      style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid="url-preview-section"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#8b5cf618' }}>
          <Share2 className="h-4 w-4" style={{ color: '#8b5cf6' }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>URL Preview / Social Sharing</h3>
          <p className="text-xs" style={{ color: colors.textMuted }}>Customize how your app appears when shared on social media</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: colors.textMuted }} />
        </div>
      ) : (
        <div className="space-y-4 pt-1">
          {/* Image Upload */}
          <div>
            <label className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              <Image className="h-3.5 w-3.5" />
              Preview Image
            </label>
            {previewUrl && (
              <div className="mb-3 rounded-xl overflow-hidden border" style={{ borderColor: colors.cardBorder }}>
                <img
                  src={previewUrl}
                  alt="Current OG preview"
                  className="w-full max-h-48 object-contain"
                  style={{ background: isDark ? '#0f172a' : '#f1f5f9' }}
                  data-testid="og-image-preview"
                />
              </div>
            )}
            <label
              className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors"
              style={{
                background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)',
                color: colors.accent,
                border: `1px dashed ${colors.accent}40`,
              }}
              data-testid="og-image-upload-label"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? 'Uploading...' : (previewUrl ? 'Replace Image' : 'Upload Image')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleImageUpload}
                disabled={uploading}
                className="hidden"
                data-testid="og-image-upload-input"
              />
            </label>
            <p className="text-[11px] mt-1.5" style={{ color: colors.textMuted }}>
              Recommended: 1200x630px. PNG, JPEG, or WebP. Max 5MB.
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
              Preview Title
            </label>
            <input
              value={settings.title}
              onChange={e => update('title', e.target.value)}
              placeholder="Eili - AI-Powered Biblical Research"
              className="rounded-lg px-3 py-2 text-sm w-full"
              style={inputStyle}
              data-testid="og-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
              Preview Description
            </label>
            <textarea
              value={settings.description}
              onChange={e => update('description', e.target.value)}
              placeholder="Dive deeper into Scripture with AI-powered insights..."
              rows={3}
              className="rounded-lg px-3 py-2 text-sm w-full resize-none"
              style={inputStyle}
              data-testid="og-description-input"
            />
          </div>

          {/* Save Text Fields */}
          {dirty && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl px-4 py-2 text-sm font-medium gap-2"
              style={{ background: colors.accent, color: '#fff' }}
              data-testid="og-settings-save"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}

          {/* Info Note */}
          <div className="rounded-lg p-3" style={{ background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.05)' }}>
            <p className="text-[11px] leading-relaxed" style={{ color: colors.textMuted }}>
              Changes to the preview image take effect immediately. Title and description changes require a deployment to update the static meta tags. Social platforms may cache old previews — use Facebook's Sharing Debugger or Twitter Card Validator to force a refresh.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ReflectionNotifSection() {
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState({
    enabled: false,
    send_time: '07:00',
    send_timezone: 'America/New_York',
    send_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    title: "Today's Reflection is Ready",
    body: "Today's reflection is on {topic}.",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);

  const loadDiag = () => {
    setDiagLoading(true);
    axios.get(`${API}/admin/reflection-notif-diagnostics`, { withCredentials: true })
      .then(r => setDiag(r.data))
      .catch(() => toast.error('Failed to load diagnostics'))
      .finally(() => setDiagLoading(false));
  };

  const sendTest = async () => {
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/admin/reflection-notif-test`, {}, { withCredentials: true });
      toast.success(`Test sent! Result: ${JSON.stringify(res.data.result)}`);
      loadDiag();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'America/Toronto', label: 'Eastern Time - Canada' },
    { value: 'America/Vancouver', label: 'Pacific Time - Canada' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central Europe (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Africa/Lagos', label: 'West Africa (WAT)' },
    { value: 'Africa/Nairobi', label: 'East Africa (EAT)' },
    { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
    { value: 'Asia/Jerusalem', label: 'Israel (IST)' },
    { value: 'Asia/Dubai', label: 'Gulf (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Japan (JST)' },
    { value: 'Asia/Seoul', label: 'Korea (KST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    axios.get(`${API}/admin/reflection-notif-config`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reflection-notif-config`, config, { withCredentials: true });
      toast.success('Reflection notification settings saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    background: isDark ? 'rgba(15,23,42,0.5)' : '#fff',
    border: `1px solid ${colors.cardBorder}`,
    color: colors.text,
  };

  return (
    <div
      className="rounded-2xl p-5 space-y-5"
      style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid="reflection-notif-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#14b8a618' }}>
            <BookOpen className="h-4 w-4" style={{ color: '#14b8a6' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Daily Reflection Notifications</h3>
            <p className="text-xs" style={{ color: colors.textMuted }}>Push notifications for daily reflections</p>
          </div>
        </div>
        <button
          onClick={() => update('enabled', !config.enabled)}
          className="relative w-12 h-7 rounded-full transition-all duration-200 shrink-0"
          style={{
            background: config.enabled ? colors.accent : (isDark ? '#334155' : '#d1d5db'),
          }}
          data-testid="reflection-notif-toggle"
        >
          <span
            className="absolute top-[2px] left-[2px] w-[22px] h-[22px] rounded-full bg-white transition-transform duration-200"
            style={{ transform: config.enabled ? 'translateX(20px)' : 'translateX(0)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
          />
        </button>
      </div>

      {config.enabled && !loading && (
        <div className="space-y-4 pt-1">
          {/* Timezone */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              <Globe className="h-3.5 w-3.5" />
              Timezone
            </label>
            <select
              value={config.send_timezone}
              onChange={e => update('send_timezone', e.target.value)}
              className="rounded-lg px-3 py-2 text-sm w-full appearance-none"
              style={inputStyle}
              data-testid="reflection-notif-timezone"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
              This timezone is used for both when the new reflection appears and when notifications are sent.
            </p>
          </div>

          {/* Send Time */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              <Clock className="h-3.5 w-3.5" />
              Notification Time
            </label>
            <input
              type="time"
              value={config.send_time}
              onChange={e => update('send_time', e.target.value)}
              className="rounded-lg px-3 py-2 text-sm w-full"
              style={inputStyle}
              data-testid="reflection-notif-time"
            />
            <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
              Notifications are sent at this time in the selected timezone. A new reflection also becomes available at midnight in this timezone.
            </p>
          </div>

          {/* Send Days — weekday selector. Empty = never fires. */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: colors.textMuted }}>
              <Clock className="h-3.5 w-3.5" />
              Send Days
            </label>
            <div className="flex flex-wrap gap-2" data-testid="reflection-notif-days">
              {[
                { key: 'monday', label: 'Mon' },
                { key: 'tuesday', label: 'Tue' },
                { key: 'wednesday', label: 'Wed' },
                { key: 'thursday', label: 'Thu' },
                { key: 'friday', label: 'Fri' },
                { key: 'saturday', label: 'Sat' },
                { key: 'sunday', label: 'Sun' },
              ].map(({ key, label }) => {
                const active = (config.send_days || []).includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      const current = new Set(config.send_days || []);
                      if (current.has(key)) current.delete(key);
                      else current.add(key);
                      // Preserve canonical order (Mon → Sun) when saving.
                      const order = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      update('send_days', order.filter(d => current.has(d)));
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    style={{
                      background: active ? colors.accent : 'transparent',
                      color: active ? 'white' : colors.text,
                      border: `1px solid ${active ? colors.accent : colors.border}`,
                    }}
                    data-testid={`reflection-notif-day-${key}`}
                    aria-pressed={active}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
              Tap a day to include or exclude it. Notifications fire only on selected days at the time above.
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
              Notification Title
            </label>
            <input
              value={config.title}
              onChange={e => update('title', e.target.value)}
              className="rounded-lg px-3 py-2 text-sm w-full"
              style={inputStyle}
              data-testid="reflection-notif-title"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
              Default Body Text
            </label>
            <input
              value={config.body}
              onChange={e => update('body', e.target.value)}
              className="rounded-lg px-3 py-2 text-sm w-full"
              style={inputStyle}
              data-testid="reflection-notif-body"
            />
            <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
              If today's reflection exists, the body will show: "Today's reflection is on [topic]."
            </p>
          </div>

          {/* Save */}
          {dirty && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl px-4 py-2 text-sm font-medium gap-2"
              style={{ background: colors.accent, color: '#fff' }}
              data-testid="reflection-notif-save"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      )}

      {/* Diagnostics & Test */}
      {config.enabled && !loading && (
        <div className="pt-2 space-y-3" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
          <div className="flex items-center gap-2">
            <button
              onClick={loadDiag}
              disabled={diagLoading}
              className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: colors.accent, background: `${colors.accent}15` }}
              data-testid="reflection-notif-diag-btn"
            >
              {diagLoading ? 'Checking...' : 'Run Diagnostics'}
            </button>
            <button
              onClick={sendTest}
              disabled={testSending}
              className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
              style={{ color: '#10b981', background: 'rgba(16,185,129,0.1)' }}
              data-testid="reflection-notif-test-btn"
            >
              {testSending ? 'Sending...' : 'Send Test Now'}
            </button>
          </div>
          {diag && (
            <div className="rounded-lg p-3 text-xs space-y-1.5 font-mono" style={{ background: isDark ? 'rgba(15,23,42,0.6)' : '#f1f5f9', color: colors.text }}>
              <div className="font-semibold text-[11px] uppercase tracking-wide mb-2" style={{ color: colors.textMuted }}>Pipeline Status</div>
              <div>Enabled: <span style={{ color: diag.config?.enabled ? '#10b981' : '#ef4444' }}>{diag.config?.enabled ? 'YES' : 'NO'}</span></div>
              <div>Time: {diag.config?.send_time} ({diag.config?.send_timezone})</div>
              <div>Current time in TZ: {diag.current_time_in_tz}</div>
              <div>Time ready: <span style={{ color: diag.time_check?.ready_to_send ? '#10b981' : '#f59e0b' }}>{diag.time_check?.description}</span></div>
              <div>VAPID keys: <span style={{ color: diag.vapid_configured ? '#10b981' : '#ef4444' }}>{diag.vapid_configured ? 'OK' : 'MISSING'}</span></div>
              <div>Users opted in: <span style={{ color: diag.users_opted_in > 0 ? '#10b981' : '#f59e0b' }}>{diag.users_opted_in}</span></div>
              <div>Opted in with push sub: <span style={{ color: diag.opted_in_with_push_sub > 0 ? '#10b981' : '#ef4444' }}>{diag.opted_in_with_push_sub}</span></div>
              <div>Total push subscriptions: <span style={{ color: diag.total_push_subscriptions > 0 ? '#10b981' : '#f59e0b' }}>{diag.total_push_subscriptions}</span></div>
              <div>Sent today ({diag.today_date}): {diag.sent_today ? `YES (${diag.sent_today.sent_count} sent)` : 'NO'}</div>
              <div>Last sent ever: {diag.last_sent_ever ? `${diag.last_sent_ever.date_key} (${diag.last_sent_ever.sent_count} sent)` : 'NEVER'}</div>
              {diag.pipeline_summary && (
                <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
                  <div className="font-semibold text-[11px] uppercase tracking-wide mb-1" style={{ color: colors.textMuted }}>Will Fire Next Check?</div>
                  <span style={{ color: diag.pipeline_summary.will_fire ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                    {diag.pipeline_summary.will_fire ? 'YES' : 'NO'}
                  </span>
                  {!diag.pipeline_summary.will_fire && (
                    <span style={{ color: '#f59e0b' }}>
                      {' '}— Missing: {[
                        !diag.pipeline_summary.enabled && 'not enabled',
                        !diag.pipeline_summary.vapid_ok && 'VAPID keys',
                        !diag.pipeline_summary.has_users && 'opted-in users',
                        !diag.pipeline_summary.has_subs && 'push subscriptions',
                        !diag.pipeline_summary.not_yet_sent_today && 'already sent today',
                        !diag.pipeline_summary.time_ready && 'time not reached',
                      ].filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AutoNotificationsSection() {
  const { isDark, colors } = useAdminTheme();
  const [config, setConfig] = useState({
    gift_enabled: true, gift_title: "You've Received a Gift!", gift_body: "You've been upgraded to {tier} for {days} days. Enjoy!",
    expiry_enabled: true, expiry_title: "Your Subscription Has Ended", expiry_body: "Your {tier} subscription has ended. Upgrade to keep your premium features.",
    expiring_soon_enabled: true, expiring_soon_days: 2, expiring_soon_title: "Subscription Expiring Soon", expiring_soon_body: "Your {tier} plan expires in {days} days. Renew to keep your features.",
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/auto-notification-config`, { withCredentials: true }).then(r => setConfig(r.data)).catch(() => {}),
      axios.get(`${API}/admin/notification-log?limit=20`, { withCredentials: true }).then(r => setLogs(r.data.notifications || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/auto-notification-config`, config, { withCredentials: true });
      toast.success('Notification settings saved');
      setDirty(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const upd = (key, val) => { setConfig(prev => ({ ...prev, [key]: val })); setDirty(true); };

  const rowStyle = { background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` };
  const inputStyle = { background: isDark ? 'rgba(15,23,42,0.5)' : '#f1f5f9', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, color: colors.text };

  const NotifBlock = ({ label, desc, enabledKey, titleKey, bodyKey, extra }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between p-3 rounded-xl" style={rowStyle}>
        <div>
          <p className="text-sm font-medium" style={{ color: colors.text }}>{label}</p>
          <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>{desc}</p>
        </div>
        <button onClick={() => upd(enabledKey, !config[enabledKey])} className="shrink-0 ml-4">
          {config[enabledKey] ? <ToggleRight className="h-7 w-7 text-green-400" /> : <ToggleLeft className="h-7 w-7" style={{ color: colors.textMuted }} />}
        </button>
      </div>
      {config[enabledKey] && (
        <div className="grid grid-cols-1 gap-2 pl-4">
          <input value={config[titleKey]} onChange={e => upd(titleKey, e.target.value)} className="w-full rounded-xl px-3 py-2 text-sm focus:outline-none" style={inputStyle} placeholder="Title" />
          <input value={config[bodyKey]} onChange={e => upd(bodyKey, e.target.value)} className="w-full rounded-xl px-3 py-2 text-xs focus:outline-none" style={inputStyle} placeholder="Body (use {tier}, {days})" />
          {extra}
        </div>
      )}
    </div>
  );

  const TYPE_LABELS = { gift_subscription: 'Gift', subscription_expired: 'Expired', expiring_soon: 'Expiring Soon' };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="auto-notifications-section">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="h-5 w-5 text-blue-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Automated Notifications</h2>
      </div>
      <p className="text-xs mb-5" style={{ color: colors.textMuted }}>Smart push notifications sent automatically based on subscription events</p>

      {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" /> : (
        <>
          <div className="space-y-4">
            <NotifBlock label="Gift Subscription" desc="When a user receives a gifted tier" enabledKey="gift_enabled" titleKey="gift_title" bodyKey="gift_body" />
            <NotifBlock label="Subscription Expired" desc="When a subscription ends" enabledKey="expiry_enabled" titleKey="expiry_title" bodyKey="expiry_body" />
            <NotifBlock label="Expiring Soon Reminder" desc="Remind users before their subscription expires" enabledKey="expiring_soon_enabled" titleKey="expiring_soon_title" bodyKey="expiring_soon_body"
              extra={
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: colors.textMuted }}>Days before:</span>
                  <input type="number" value={config.expiring_soon_days} onChange={e => upd('expiring_soon_days', parseInt(e.target.value) || 2)} min={1} max={14}
                    className="w-16 rounded-xl px-3 py-1.5 text-sm focus:outline-none" style={inputStyle} />
                </div>
              }
            />
          </div>

          <div className="flex items-center justify-between mt-5">
            <button onClick={() => setShowLogs(!showLogs)} className="text-xs font-medium" style={{ color: colors.accent }}>
              {showLogs ? 'Hide' : 'View'} Recent Activity ({logs.length})
            </button>
            <Button onClick={handleSave} disabled={saving || !dirty}
              className="rounded-xl bg-blue-600 hover:bg-blue-500 text-white gap-2 disabled:opacity-40" data-testid="save-auto-notif-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Settings
            </Button>
          </div>

          {showLogs && logs.length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto rounded-xl divide-y" style={{ background: isDark ? 'rgba(15,23,42,0.4)' : '#f8fafc', borderColor: colors.cardBorder }}>
              {logs.map((l, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs" style={{ borderColor: isDark ? '#1e293b' : '#e2e8f0' }}>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: `${colors.accent}15`, color: colors.accent }}>{TYPE_LABELS[l.type] || l.type}</span>
                  <span className="flex-1 truncate" style={{ color: colors.textMuted }}>{l.user_id}</span>
                  <span style={{ color: colors.textMuted }}>{new Date(l.sent_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function AdminPushSection() {
  const { colors } = useAdminTheme();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already subscribed
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          setSubscribed(!!sub);
        });
      });
    }
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (subscribed) {
        // Unsubscribe
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
        await axios.delete(`${API}/admin/push/unsubscribe`, { withCredentials: true });
        setSubscribed(false);
        toast.success('Push notifications disabled');
      } else {
        // Subscribe
        const res = await axios.get(`${API}/notifications/vapid-public-key`, { withCredentials: true });
        const vapidKey = res.data.public_key;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        await axios.post(`${API}/admin/push/subscribe`, { subscription: sub.toJSON() }, { withCredentials: true });
        setSubscribed(true);
        toast.success('Push notifications enabled! You\'ll be notified when users are locked out.');
      }
    } catch (err) {
      if (Notification.permission === 'denied') {
        toast.error('Notifications are blocked by your browser. Enable them in browser settings.');
      } else {
        toast.error('Failed to update push settings');
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper: convert VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
  }

  const supported = 'serviceWorker' in navigator && 'PushManager' in window;

  return (
    <div className="rounded-2xl p-6 space-y-4" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="admin-push-section">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4" style={{ color: colors.accent }} />
        <h2 className="text-sm font-semibold" style={{ color: colors.text }}>Admin Push Notifications</h2>
      </div>
      <p className="text-xs" style={{ color: colors.textMuted }}>
        Get instant push notifications when a user account gets permanently locked and needs support.
      </p>
      {supported ? (
        <Button
          onClick={handleToggle}
          disabled={loading}
          className={`rounded-xl gap-2 text-xs ${subscribed ? 'bg-emerald-600 hover:bg-emerald-500' : ''}`}
          style={!subscribed ? { background: colors.accent } : undefined}
          data-testid="admin-push-toggle"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : subscribed ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
          {subscribed ? 'Notifications Enabled' : 'Enable Notifications'}
        </Button>
      ) : (
        <p className="text-xs text-amber-400">Push notifications are not supported in this browser.</p>
      )}
    </div>
  );
}


function FeatureFlagsSection() {
  const { colors } = useAdminTheme();
  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="feature-flags-section">
      <div className="flex items-center gap-2 mb-3">
        <Flag className="h-5 w-5 text-indigo-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Feature Flags</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
        Feature flags now live in their own dedicated page so you can ship features dark and roll them out on your timing.
      </p>
      <a
        href="/admin/feature-flags"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
        style={{ background: colors.accent, color: '#fff' }}
        data-testid="feature-flags-section-link"
      >
        Open Feature Flags →
      </a>
    </div>
  );
}


const ROLE_META = [
  { key: 'super_admin', label: 'Super Admin', color: '#fbbf24' },
  { key: 'admin', label: 'Admin', color: '#818cf8' },
  { key: 'viewer', label: 'Viewer', color: '#94a3b8' },
];

function AdminDeviceLimitsSection() {
  const { isDark, colors } = useAdminTheme();
  const [limits, setLimits] = useState({ super_admin: 3, admin: 2, viewer: 1 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/device-limits`, { withCredentials: true })
      .then(res => { setLimits(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleChange = (role, value) => {
    const parsed = value === '' ? '' : Math.max(1, Math.min(10, parseInt(value, 10) || 1));
    setLimits(prev => ({ ...prev, [role]: parsed }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/device-limits`, limits, { withCredentials: true });
      toast.success('Admin device limits updated');
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const rowStyle = {
    background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <div className="rounded-2xl p-6" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="admin-device-limits-section">
      <div className="flex items-center gap-2 mb-1">
        <Monitor className="h-5 w-5 text-cyan-400" />
        <h2 className="text-base font-semibold" style={{ color: colors.text }}>Admin Device Limits</h2>
      </div>
      <p className="text-xs mb-5" style={{ color: colors.textMuted }}>
        Set the maximum simultaneous devices for each admin role. When a new device signs in beyond the limit, the admin must replace an existing device.
      </p>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />
      ) : (
        <>
          <div className="space-y-3">
            {ROLE_META.map(r => (
              <div key={r.key} className="flex items-center justify-between p-3 rounded-xl" style={rowStyle} data-testid={`admin-device-limit-${r.key}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${r.color}18` }}>
                    <span className="text-xs font-bold" style={{ color: r.color }}>{limits[r.key]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: r.color }}>{r.label}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{limits[r.key]} device{limits[r.key] !== 1 ? 's' : ''} max</p>
                  </div>
                </div>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={limits[r.key] === '' ? '' : limits[r.key]}
                  onChange={e => handleChange(r.key, e.target.value)}
                  className="w-16 rounded-lg border px-3 py-1.5 text-sm text-center focus:outline-none focus:border-indigo-500 transition"
                  style={{ background: colors.input, borderColor: colors.inputBorder, color: colors.text }}
                  data-testid={`admin-device-limit-input-${r.key}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white gap-2 disabled:opacity-40"
              data-testid="save-admin-device-limits-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Limits
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function InactivityTimeoutSection() {
  const { isDark, colors } = useAdminTheme();
  const { timeoutMinutes, refreshTimeout } = useAdmin();
  const [selected, setSelected] = useState(timeoutMinutes);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(timeoutMinutes); }, [timeoutMinutes]);

  const options = [
    { value: 1, label: '1 min' },
    { value: 3, label: '3 mins' },
    { value: 5, label: '5 mins' },
    { value: 15, label: '15 mins' },
    { value: 30, label: '30 mins' },
    { value: 60, label: '1 hour' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/inactivity-timeout`, { timeout_minutes: selected }, { withCredentials: true });
      refreshTimeout();
      toast.success(`Inactivity timeout set to ${options.find(o => o.value === selected)?.label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid="inactivity-timeout-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(234,179,8,0.12)' }}>
          <Gauge className="h-5 w-5 text-amber-500" />
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Session Inactivity Timeout</h3>
          <p className="text-xs" style={{ color: colors.textMuted }}>Auto-logout admins after a period of no activity</p>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
        {options.map(opt => (
          <button key={opt.value} onClick={() => setSelected(opt.value)}
            className="px-3 py-2 rounded-xl text-xs font-medium transition-all"
            style={{
              background: selected === opt.value ? (isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)') : (isDark ? 'rgba(51,65,85,0.4)' : '#f1f5f9'),
              border: `1.5px solid ${selected === opt.value ? '#6366f1' : 'transparent'}`,
              color: selected === opt.value ? '#6366f1' : colors.textSecondary,
            }}
            data-testid={`timeout-option-${opt.value}`}>
            {opt.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: colors.textMuted }}>
          Current: <span className="font-medium" style={{ color: colors.text }}>{options.find(o => o.value === timeoutMinutes)?.label || `${timeoutMinutes} min`}</span>
        </p>
        <Button onClick={handleSave} disabled={saving || selected === timeoutMinutes} size="sm"
          className="rounded-xl gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40"
          data-testid="save-timeout-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}
